export class ClipboardHelper {
  static canUseDeferredClipboardWrite(): boolean {
    return (
      typeof window !== 'undefined' &&
      !!window.isSecureContext &&
      !!navigator?.clipboard?.write &&
      typeof ClipboardItem !== 'undefined'
    );
  }

  static beginDeferredWrite(): {
    resolve: (text: string) => void;
    cancel: () => void;
    result: Promise<boolean>;
  } {
    let settleText: (text: string | null) => void;
    const textReady = new Promise<string | null>((resolve) => {
      settleText = resolve;
    });

    if (!ClipboardHelper.canUseDeferredClipboardWrite()) {
      return {
        resolve: (text: string) => settleText(text),
        cancel: () => settleText(null),
        result: textReady.then((text) => (text ? ClipboardHelper.copyText(text) : false)),
      };
    }

    let settled = false;
    let settleResult: (ok: boolean) => void;
    const result = new Promise<boolean>((resolve) => {
      settleResult = (ok) => {
        if (!settled) {
          settled = true;
          resolve(ok);
        }
      };
    });

    const blobReady = textReady.then((text) => {
      if (!text) {
        throw new DOMException('Clipboard write aborted', 'AbortError');
      }
      return new Blob([text], { type: 'text/plain' });
    });
    void blobReady.catch(() => undefined);

    navigator.clipboard
      .write([new ClipboardItem({ 'text/plain': blobReady })])
      .then(async () => {
        const text = await textReady;
        settleResult(!!text);
      })
      .catch(async (err) => {
        const text = await textReady;
        if (text) {
          console.warn('Deferred clipboard.write failed, trying fallback', err);
          settleResult(await ClipboardHelper.copyText(text));
          return;
        }
        settleResult(false);
      });

    return {
      resolve: (text: string) => settleText(text),
      cancel: () => {
        settleResult(false);
      },
      result,
    };
  }

  static async copyText(text: string): Promise<boolean> {
    if (!text) {
      return false;
    }

    if (typeof window !== 'undefined' && window.isSecureContext && navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn('navigator.clipboard.writeText failed, trying fallback', err);
      }
    }

    return ClipboardHelper.copyTextFallback(text);
  }

  private static copyTextFallback(text: string): boolean {
    let succeeded = false;
    const onCopy = (event: ClipboardEvent) => {
      event.clipboardData?.setData('text/plain', text);
      event.preventDefault();
      succeeded = true;
    };

    try {
      document.addEventListener('copy', onCopy);
      if (document.execCommand('copy')) {
        return true;
      }
    } catch (err) {
      console.warn('copy-event fallback failed', err);
    } finally {
      document.removeEventListener('copy', onCopy);
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.cssText =
        'position:fixed;top:0;left:0;width:2px;height:2px;padding:0;border:none;outline:none;opacity:0;';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      succeeded = document.execCommand('copy');
      document.body.removeChild(textarea);
      return succeeded;
    } catch (err) {
      console.error('Clipboard textarea fallback failed', err);
      return false;
    }
  }
}
